import { z } from 'zod';
import { HOME_TYPES, HomeView, STARTER_FURNITURE } from './home';

// Houses (as Lagos Life's 🔑 Houses, worked out from its public client code): the homes a player can live in are
// content the admins edit. A player rents one (two weeks' rent up front and the agent's fee to move in) or buys it
// outright (a year of its rent: no rent, ever, and it counts in their money at what they paid); an owned home
// can be extended, a few steps bigger, by a crew that takes some hours; it can be sold back, and they stay on in
// it as a tenant. Furniture comes along when they move; what does not fit is put away.

const colour = z.string().regex(/^#[0-9a-f]{6}$/i);
const pence = (max: number) => z.number().int().min(0).max(max);
const Placed = z.object({
  item: z.string().min(1).max(40),
  x: z.number().min(-20).max(20),
  z: z.number().min(-20).max(20),
  rotation: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
});

export const HomeKind = z.object({
  id: z.string().regex(/^[a-z0-9_]{2,30}$/),
  label: z.string().trim().min(2).max(40),
  area: z.string().trim().min(2).max(40),
  description: z.string().trim().min(2).max(160),
  icon: z.string().trim().min(1).max(16),
  // the room's floor in metres, and how many friends fit for a visit
  floor: z.object({ w: z.number().min(3).max(16), d: z.number().min(3).max(16) }),
  guests: z.number().int().min(1).max(30),
  rentPenceWeekly: pence(100_000_000),
  wallColour: colour,
  floorColour: colour,
  // how many times it can be extended once owned (0: never)
  extensions: z.number().int().min(0).max(4),
  // what it comes with on a player's first day there
  starter: z.array(Placed).max(30),
  // on the market for players to move to (a home someone lives in keeps working when it is taken off)
  visible: z.boolean(),
  // how sought-after it is as a home to let (1 to 4): a better tier costs more to buy to let
  tier: z.number().int().min(1).max(4).optional(),
});
export type HomeKind = z.infer<typeof HomeKind>;

export const HousesRules = z.object({
  // moving in to rent: this many weeks' rent up front, and the agent's fee in weeks of rent
  upfrontWeeks: z.number().int().min(0).max(12),
  agentWeeks: z.number().min(0).max(12),
  // buying: this many weeks of its rent; selling back: this share of what was paid
  buyWeeks: z.number().int().min(1).max(520),
  sellShare: z.number().min(0).max(1),
  // each extension: metres added to the width and the depth, its price (this share of the buy price for each
  // part of the floor added), and the hours the crew takes for the first, second, … extension
  extendMetres: z.number().min(0.5).max(4),
  extendShare: z.number().min(0).max(2),
  extendHours: z.array(z.number().min(0).max(72)).min(1).max(4),
  // the cheapest home a player is moved to when evicted (the kind's id)
  fallback: z.string().regex(/^[a-z0-9_]{2,30}$/),
});
export type HousesRules = z.infer<typeof HousesRules>;

// buy to let (as Lagos Life's Invest → houses): a home bought to rent out costs weeks of its rent (more for a
// better tier), an agent finds tenants each evening (fewer the dearer the rent asked), and it sells back for a
// share of what was paid
export const RentalRules = z.object({
  visible: z.boolean(),
  maxProperties: z.number().int().min(1).max(50),
  priceWeeks: z.number().min(1).max(520),
  tierShare: z.array(z.number().min(0.1).max(5)).length(4),
  sellShare: z.number().min(0).max(1),
  // the rent asked, against the going rate
  bandLow: z.number().min(0.1).max(1),
  bandHigh: z.number().min(1).max(5),
  // the share of evenings let: full at or below `fullAt` of the going rate, `atGoing` at it, `atHigh` at the top
  fullAt: z.number().min(0.1).max(1),
  atGoing: z.number().min(0).max(1),
  atHigh: z.number().min(0).max(1),
  // the agent's fee on the rent, and at most this many evenings caught up for a player who was away
  agentShare: z.number().min(0).max(0.5),
  catchUp: z.number().int().min(1).max(60),
  buysPerDay: z.number().int().min(1).max(100),
});
export type RentalRules = z.infer<typeof RentalRules>;
export const DEFAULT_RENTALS: RentalRules = {
  visible: true,
  maxProperties: 5,
  priceWeeks: 12,
  tierShare: [0.9, 1, 1.05, 1.15],
  sellShare: 0.8,
  bandLow: 0.7,
  bandHigh: 1.5,
  fullAt: 0.85,
  atGoing: 0.85,
  atHigh: 0.15,
  agentShare: 0,
  catchUp: 21,
  buysPerDay: 10,
};

export const HousesContent = z.object({
  kinds: z.array(HomeKind).min(1).max(40),
  rules: HousesRules,
  rentals: RentalRules.optional(),
});
export type HousesContent = z.infer<typeof HousesContent>;

// the homes the game started with, and the bigger ones on the market (Lagos's Face-me-I-face-you to its
// Banana Island mansion, the London way)
const STARTING = (Object.keys(HOME_TYPES) as (keyof typeof HOME_TYPES)[]).map((id): HomeKind => ({
  id,
  label: HOME_TYPES[id].label,
  area: HOME_TYPES[id].area,
  description: HOME_TYPES[id].description,
  icon: id === 'flatshare_room' ? '🛏️' : id === 'studio' ? '🏢' : '🏘️',
  floor: { ...HOME_TYPES[id].floor },
  guests: id === 'flatshare_room' ? 3 : 5,
  rentPenceWeekly: HOME_TYPES[id].rentPenceWeekly,
  wallColour: HOME_TYPES[id].wallColour,
  floorColour: HOME_TYPES[id].floorColour,
  extensions: id === 'flatshare_room' ? 0 : 2,
  starter: STARTER_FURNITURE[id].map((thing) => ({ ...thing })),
  visible: true,
  tier: id === 'studio' ? 2 : 1,
}));
export const DEFAULT_HOUSES: HousesContent = {
  kinds: [
    ...STARTING,
    {
      id: 'one_bed',
      label: 'One-bed flat',
      area: 'Canary Wharf',
      description: 'A proper flat by the towers: a bedroom, a living room with a view, a kitchen and a bathroom.',
      icon: '🌆',
      floor: { w: 7.4, d: 5.6 },
      guests: 8,
      rentPenceWeekly: 52_000,
      wallColour: '#e9eef2',
      floorColour: '#9b8466',
      extensions: 3,
      starter: [],
      visible: true,
      tier: 3,
    },
    {
      id: 'terrace',
      label: 'Terraced house',
      area: 'Greenwich',
      description: 'Two floors of Victorian brick near the park and the river. Room to grow, and neighbours who wave.',
      icon: '🏡',
      floor: { w: 8.4, d: 6.4 },
      guests: 10,
      rentPenceWeekly: 95_000,
      wallColour: '#f2e8da',
      floorColour: '#8a6a4c',
      extensions: 3,
      starter: [],
      visible: true,
      tier: 3,
    },
    {
      id: 'penthouse',
      label: 'River penthouse',
      area: 'Royal Docks',
      description: 'The top floor, glass all round, the docks and the planes below. Big rent, bigger parties.',
      icon: '🏙️',
      floor: { w: 10, d: 7.6 },
      guests: 16,
      rentPenceWeekly: 450_000,
      wallColour: '#eef1f4',
      floorColour: '#c9c2b6',
      extensions: 2,
      starter: [],
      visible: true,
      tier: 4,
    },
    {
      id: 'mansion',
      label: 'Mansion',
      area: 'Blackheath',
      description: 'A detached house on the heath with its own gates. London Life’s top address.',
      icon: '🏰',
      floor: { w: 12, d: 9 },
      guests: 24,
      rentPenceWeekly: 1_500_000,
      wallColour: '#f4efe6',
      floorColour: '#7a5a3c',
      extensions: 2,
      starter: [],
      visible: true,
      tier: 4,
    },
  ],
  rules: {
    upfrontWeeks: 2,
    agentWeeks: 1,
    buyWeeks: 52,
    sellShare: 0.8,
    extendMetres: 1.2,
    extendShare: 0.85,
    extendHours: [2, 2.5, 3],
    fallback: 'council_flat',
  },
  rentals: DEFAULT_RENTALS,
};
export const housesOf = (content: { houses?: HousesContent }) => content.houses ?? DEFAULT_HOUSES;
export function housesProblems(houses: HousesContent): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  for (const kind of houses.kinds) {
    if (ids.has(kind.id)) problems.push(`Two homes have the id "${kind.id}".`);
    ids.add(kind.id);
  }
  for (const id of Object.keys(HOME_TYPES))
    if (!ids.has(id)) problems.push(`The starting home "${id}" cannot be removed (hide it instead).`);
  if (!ids.has(houses.rules.fallback)) problems.push('The home players are moved to when evicted is not a home.');
  return problems;
}

// what a home costs to buy (a year of its rent by default), to move in to as a tenant, and to extend
export const homeBuyPence = (kind: HomeKind, rules: HousesRules) =>
  Math.round((kind.rentPenceWeekly * rules.buyWeeks) / 100) * 100;
export const moveInPence = (kind: HomeKind, rules: HousesRules) =>
  Math.round(kind.rentPenceWeekly * (rules.upfrontWeeks + rules.agentWeeks));
// the floor after this many extensions
export const floorOf = (kind: Pick<HomeKind, 'floor'>, rules: HousesRules, extended: number) => ({
  w: Math.round((kind.floor.w + rules.extendMetres * extended) * 100) / 100,
  d: Math.round((kind.floor.d + rules.extendMetres * extended) * 100) / 100,
});
// the next extension: its price (a share of the buy price for each part of the floor it adds) and its hours
export function nextExtension(kind: HomeKind, rules: HousesRules, extended: number) {
  if (extended >= kind.extensions) return null;
  const now = floorOf(kind, rules, extended);
  const next = floorOf(kind, rules, extended + 1);
  const base = kind.floor.w * kind.floor.d;
  const added = next.w * next.d - now.w * now.d;
  return {
    floor: next,
    pricePence: Math.round((homeBuyPence(kind, rules) * (added / base) * rules.extendShare) / 100) * 100,
    hours: rules.extendHours[Math.min(extended, rules.extendHours.length - 1)]!,
  };
}
export const sellBackPence = (paidPence: number, rules: HousesRules) => Math.floor(paidPence * rules.sellShare);
export const homeKindOf = (houses: HousesContent, id: string) =>
  houses.kinds.find((kind) => kind.id === id) ?? houses.kinds.find((kind) => kind.id === houses.rules.fallback)!;

// the room a home of this kind is, after this many extensions
export const homeRoomOf = (houses: HousesContent, home: { type: string; extended: number }) => {
  const kind = homeKindOf(houses, home.type);
  return {
    floor: floorOf(kind, houses.rules, home.extended),
    wallColour: kind.wallColour,
    floorColour: kind.floorColour,
  };
};

// ---------- over the wire ----------

// a home on the market: renting it (moving in) or buying it
export const MarketHome = z.object({
  id: z.string(),
  label: z.string(),
  area: z.string(),
  description: z.string(),
  icon: z.string(),
  floor: z.object({ w: z.number(), d: z.number() }),
  guests: z.number().int(),
  rentPenceWeekly: z.number().int(),
  moveInPence: z.number().int(),
  buyPence: z.number().int(),
  extensions: z.number().int(),
  // the one the player lives in
  current: z.boolean(),
});
export type MarketHome = z.infer<typeof MarketHome>;
export const MoveHomeRequest = z.object({ kind: z.string().regex(/^[a-z0-9_]{2,30}$/), buy: z.boolean() });
export type MoveHomeRequest = z.infer<typeof MoveHomeRequest>;
export const HouseAction = z.enum(['buy', 'sell', 'extend']);
export type HouseAction = z.infer<typeof HouseAction>;
export const HousesReply = z.object({
  home: HomeView,
  walletPence: z.number().int(),
  market: z.array(MarketHome),
  rules: HousesRules,
  message: z.string().optional(),
});
export type HousesReply = z.infer<typeof HousesReply>;

// ---------- buy to let ----------

export const rentalsOf = (houses: HousesContent) => houses.rentals ?? DEFAULT_RENTALS;
// two significant figures, as an estate agent prices
const roundPrice = (pence: number) => {
  if (!(pence > 0)) return 0;
  const step = Math.pow(10, Math.max(2, Math.floor(Math.log10(pence)) - 1));
  return Math.round(pence / step) * step;
};
export const letPricePence = (kind: HomeKind, rentals: RentalRules) =>
  roundPrice(kind.rentPenceWeekly * rentals.priceWeeks * rentals.tierShare[(kind.tier ?? 2) - 1]!);
export const rentBand = (kind: HomeKind, rentals: RentalRules) => ({
  going: kind.rentPenceWeekly,
  low: Math.ceil((kind.rentPenceWeekly * rentals.bandLow) / 100) * 100,
  high: Math.floor((kind.rentPenceWeekly * rentals.bandHigh) / 100) * 100,
});
// the share of evenings it is let: full when cheap, less the dearer the rent asked
export function occupancy(askedPence: number, goingPence: number, rentals: RentalRules) {
  if (!(askedPence > 0) || !(goingPence > 0)) return 0;
  const ratio = askedPence / goingPence;
  const points: [number, number][] = [
    [rentals.fullAt, 1],
    [1, rentals.atGoing],
    [rentals.bandHigh, rentals.atHigh],
  ];
  if (ratio <= points[0]![0]) return 1;
  for (let index = 1; index < points.length; index += 1) {
    const [x0, y0] = points[index - 1]!;
    const [x1, y1] = points[index]!;
    if (ratio <= x1) return y0 + ((y1 - y0) * (ratio - x0)) / (x1 - x0);
  }
  return points.at(-1)![1];
}
// an evening's rent from the agent's tenants (a seventh of the week, as often as it is let), less the agent
export const eveningRentPence = (askedPence: number, goingPence: number, rentals: RentalRules) =>
  Math.floor(((askedPence * occupancy(askedPence, goingPence, rentals)) / 7) * (1 - rentals.agentShare));

export const RentalView = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  label: z.string(),
  area: z.string(),
  icon: z.string(),
  paidPence: z.number().int(),
  sellPence: z.number().int(),
  askedPence: z.number().int(),
  band: z.object({ going: z.number().int(), low: z.number().int(), high: z.number().int() }),
  occupancy: z.number(),
  eveningPence: z.number().int(),
  earnedPence: z.number().int(),
  boughtAt: z.string(),
});
export type RentalView = z.infer<typeof RentalView>;
export const RentalOffer = z.object({
  kind: z.string(),
  label: z.string(),
  area: z.string(),
  icon: z.string(),
  pricePence: z.number().int(),
  goingPence: z.number().int(),
  // a week at the going rent, as the agent lets it
  weekPence: z.number().int(),
});
export type RentalOffer = z.infer<typeof RentalOffer>;
export const RentalsReply = z.object({
  rentals: z.array(RentalView),
  offers: z.array(RentalOffer),
  walletPence: z.number().int(),
  maxProperties: z.number().int(),
  message: z.string().optional(),
});
export type RentalsReply = z.infer<typeof RentalsReply>;
export const BuyRentalRequest = z.object({ kind: z.string().regex(/^[a-z0-9_]{2,30}$/) });
export type BuyRentalRequest = z.infer<typeof BuyRentalRequest>;
export const RentalActionRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('rent'), askedPence: z.number().int().positive().max(1_000_000_000) }),
  z.object({ action: z.literal('sell') }),
]);
export type RentalActionRequest = z.infer<typeof RentalActionRequest>;
